import { useEffect, useRef, useState } from "react";
import jsQR from "jsqr";
import { ReactComponent as ModalCloseIcon } from "../../assets/svg/close-modal.svg";

const QRScannerModal = ({ onSuccess, onCancel }) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const animFrameRef = useRef(null);
  const [scanning, setScanning] = useState(true);
  const [detected, setDetected] = useState(null);
  const [error, setError] = useState("");

  const activeRef = useRef(true);

  useEffect(() => {
    activeRef.current = true;
    startCamera();
    return () => {
      activeRef.current = false;
      stopCamera();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startCamera = () => {
    navigator.mediaDevices
      .getUserMedia({ video: { facingMode: "environment" } })
      .then((stream) => {
        if (!activeRef.current) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          const playPromise = videoRef.current.play();
          if (playPromise !== undefined) {
            playPromise.catch((err) => {
              // Interrupted by new load request or other issues
              console.log("Video play promise caught:", err.message);
            });
          }
        }
        requestAnimationFrame(scanFrame);
      })
      .catch((err) => {
        if (activeRef.current) {
          console.error("Camera error:", err);
          setError("Camera access denied. Please allow camera permission.");
        }
      });
  };

  const stopCamera = () => {
    if (animFrameRef.current) {
      cancelAnimationFrame(animFrameRef.current);
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  };

  const scanFrame = () => {
    if (!activeRef.current) return;
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas || video.readyState !== video.HAVE_ENOUGH_DATA) {
      animFrameRef.current = requestAnimationFrame(scanFrame);
      return;
    }

    const ctx = canvas.getContext("2d");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const code = jsQR(imageData.data, imageData.width, imageData.height, {
      inversionAttempts: "dontInvert",
    });

    if (code && code.data) {
      stopCamera();
      onSuccess(code.data);
      return;
    }

    animFrameRef.current = requestAnimationFrame(scanFrame);
  };

  const handleConfirm = () => {
    if (detected) {
      onSuccess(detected);
    }
  };

  const handleRetry = () => {
    setDetected(null);
    setScanning(true);
    setError("");
    startCamera();
  };

  const handleCancel = () => {
    stopCamera();
    onCancel();
  };

  const overlayStyle = {
    position: "fixed",
    top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: "rgba(0,0,0,0.65)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 1200,
  };

  const modalStyle = {
    backgroundColor: "#fff",
    borderRadius: "20px",
    padding: "20px",
    width: "340px",
    boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
    fontFamily: "Epilogue, sans-serif",
  };

  return (
    <div style={overlayStyle}>
      <div style={modalStyle}>
        {/* Header */}
        <div className="text-center mb-4">
          <p className="m-0" style={{ fontWeight: "700", fontSize: "18px", color: "#212129" }}>
            Place the QR code inside the area
          </p>
        </div>

        {/* Error State */}
        {error && (
          <div className="text-center py-4">
            <div style={{ fontSize: "40px", marginBottom: "12px" }}>📷</div>
            <p style={{ color: "#D35258", fontWeight: "600", fontSize: "14px" }}>{error}</p>
            <button
              onClick={handleCancel}
              style={{ border: "1px solid #E5E5E5", borderRadius: "50px", padding: "8px 24px", background: "#fff", fontWeight: "600", fontSize: "13px", cursor: "pointer", color: "#172B4D" }}
            >
              Close
            </button>
          </div>
        )}

        {/* Camera / Scan View */}
        {!error && (
          <div>
            <div style={{ position: "relative", width: "100%", paddingBottom: "100%", overflow: "hidden", borderRadius: "12px", background: "#000" }}>
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", objectFit: "cover" }}
              />
            </div>

            {/* Bottom Actions */}
            <div className="d-flex justify-content-between align-items-center mt-4 px-2">
              {/* Close Button */}
              <div 
                onClick={handleCancel} 
                style={{ width: '56px', height: '56px', borderRadius: '50%', border: '1px solid #D4DDE7', background: '#FFFFFF', display: 'flex', justifyContent: 'center', alignItems: 'center', cursor: 'pointer', boxShadow: "0 2px 8px rgba(0,0,0,0.05)" }}
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#212129" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </div>

              {/* Refresh / Retry Button */}
              <div 
                onClick={handleRetry} 
                style={{ width: '56px', height: '56px', borderRadius: '50%', border: '1px solid #D4DDE7', background: '#FFFFFF', display: 'flex', justifyContent: 'center', alignItems: 'center', cursor: 'pointer', boxShadow: "0 2px 8px rgba(0,0,0,0.05)" }}
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#212129" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M23 4v6h-6"></path>
                  <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"></path>
                </svg>
              </div>
            </div>
          </div>
        )}

        <canvas ref={canvasRef} style={{ display: "none" }} />
      </div>
    </div>
  );
};

export default QRScannerModal;
